import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { useTrips } from "@/hooks/useTrips";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PlusCircle, UserPlus, Copy, Share2 } from "lucide-react";
import { toast } from "sonner";

export const TripDialog = () => {
  const [open, setOpen] = useState(false);
  const [tripName, setTripName] = useState("");
  const [budget, setBudget] = useState("");
  const [shareId, setShareId] = useState("");
  const { createTrip, joinTrip } = useTrips();

  const handleCreateTrip = async () => {
    if (tripName && budget) {
      const trip = await createTrip(tripName, Number(budget));
      if (trip) {
        setShareId(trip.share_id);
        toast.success("Trip created successfully!");
      }
    }
  };

  const handleJoinTrip = async () => {
    if (shareId) {
      await joinTrip(shareId);
      setOpen(false);
      setShareId("");
    }
  };

  const copyShareId = () => {
    navigator.clipboard.writeText(shareId);
    toast.success("Share ID copied to clipboard!");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="w-full bg-gradient-to-r from-primary to-primary/80">
          <PlusCircle className="mr-2 h-4 w-4" />
          Start or Join Trip
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Manage Trip</DialogTitle>
          <DialogDescription>
            Create a new trip or join an existing one using a Share ID
          </DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="create" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="create">Create Trip</TabsTrigger>
            <TabsTrigger value="join">Join Trip</TabsTrigger>
          </TabsList>
          <TabsContent value="create" className="space-y-4 py-4">
            <Input
              placeholder="Enter trip name..."
              value={tripName}
              onChange={(e) => setTripName(e.target.value)}
              className="mb-4"
            />
            <Input
              type="number"
              placeholder="Enter budget..."
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
              className="mb-4"
            />
            <Button onClick={handleCreateTrip} className="w-full">
              <PlusCircle className="mr-2 h-4 w-4" />
              Create Trip
            </Button>

            {shareId && (
              <div className="mt-4 p-4 bg-primary/5 rounded-lg space-y-2">
                <p className="text-sm font-medium text-primary flex items-center gap-2">
                  <Share2 className="h-4 w-4" />
                  Share this ID with others:
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-2 bg-white rounded border">{shareId}</code>
                  <Button size="sm" variant="outline" onClick={copyShareId}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Others can use this ID to join and collaborate on your trip
                </p>
              </div>
            )}
          </TabsContent>
          <TabsContent value="join" className="space-y-4 py-4">
            <Input
              placeholder="Enter Share ID..."
              value={shareId}
              onChange={(e) => setShareId(e.target.value)}
              className="mb-4"
            />
            <Button onClick={handleJoinTrip} className="w-full">
              <UserPlus className="mr-2 h-4 w-4" />
              Join Trip
            </Button>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};